import React, {useEffect, useState} from 'react';
import {getProfile, getTwoFactorStatus, currentUserInterface} from "../libs/Auth";
import {useNavigate} from "react-router-dom";
import {CurrentUserProvider} from "../context/CurrentUserContext";

interface propsInterface {
    component: React.JSX.Element;
    roles: String[];
    /**
     * Pour les écrans 2FA eux-mêmes (configuration/vérification) : sans ça,
     * un compte pas encore vérifié boucle indéfiniment en essayant d'y
     * accéder.
     */
    skipTwoFactorCheck?: boolean;
}

// Rôles de l'espace d'administration : le bouton flottant CookieYes y est
// masqué (voir la règle `body.hide-cookieyes` dans index.css).
const ADMIN_ROLES = ['CITYADMIN', 'CITYREDACTOR', 'SUPERADMIN'];

// Comptes soumis au 2FA côté backend (EnsureTwoFactorVerified) — voir
// docs/01-backend.md § Authentification à deux facteurs.
const TWO_FACTOR_ROLES = ['CITYADMIN', 'CITYREDACTOR', 'CITYADMINRESTRICTED', 'SUPERADMIN'];

const AuthGuard = ({component, roles, skipTwoFactorCheck = false}: propsInterface) => {
    const [status, setStatus] = useState(false);
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState({} as currentUserInterface);
    useEffect(() => {
        checkToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [component]);

    const checkToken = async () => {
        const user = await getProfile();
        if(user){
            if(!roles.includes('ALL')){
                if(!roles.includes(user.role)){
                    navigate('/login');
                    return;
                }
            }
            // Pas de retrait au démontage : sinon le bouton réapparaît le temps
            // du getProfile à chaque navigation. UnAuthGuard le retire.
            document.body.classList.toggle('hide-cookieyes', ADMIN_ROLES.includes(user.role));

            if(!skipTwoFactorCheck && TWO_FACTOR_ROLES.includes(user.role)){
                const deuxFacteurs = await getTwoFactorStatus();
                if(deuxFacteurs?.required && !deuxFacteurs.confirmed){
                    navigate('/2fa/configuration');
                    return;
                }
                if(deuxFacteurs?.required && deuxFacteurs.confirmed && !deuxFacteurs.token_verified){
                    navigate('/2fa/verification');
                    return;
                }
            }

            setCurrentUser(user as currentUserInterface);
            setStatus(true);
        }else{
            navigate('/login');
        }
    }

    return status ? <React.Fragment><CurrentUserProvider value={currentUser}>{component}</CurrentUserProvider></React.Fragment> : <React.Fragment/>
}

export default AuthGuard;
